import { isNil, slice, sortBy } from 'lodash';

/**
 * Format sort code string into a dash-separated version
 *
 * @param {string} sortCode Source sort code
 */
export const formatSortCode = (sortCode: string) =>
  sortCode
    .replace('-', '')
    .match(/[0-9]{2}/g)
    ?.join('-') || sortCode;

/**
 * Get list of entity identifiers matching the specified range boundaries
 *
 * @param {Array} entities Source list of entities
 * @param {number} startIndex Range start index
 * @param {number} endIndex Range end index
 * @param {Function} valueMapper Entity value mapping function
 */
export const getEntityRangeIds = <TEntity, TKey = string>(
  entities: Array<TEntity>,
  startIndex: number,
  endIndex: number,
  valueMapper: (entity: TEntity) => TKey,
) => {
  const [lowerIndex, upperIndex] = sortBy([startIndex, endIndex]);
  return slice(entities, lowerIndex, upperIndex + 1).map(valueMapper);
};

/**
 * Check if the specified value is not empty
 *
 * @param {*} value Value to check
 */
export const isNotEmpty = <TValue>(value: Optional<TValue> | Nullable<TValue>): value is TValue =>
  !isNil(value);

/**
 * Check if the specified list of values contains any negative values
 *
 * @param {Array<number>} values Source values list
 */
const isNegative = (...values: Array<number>) => values.every(value => value < 0);

/**
 * Check if the specified list of values contains any positive values
 *
 * @param {Array<number>} values Source values list
 */
const isPositive = (...values: Array<number>) => values.every(value => value >= 0);

/**
 * Check if the specified list of values only contains zeroes
 *
 * @param {Array<number>} values Source values list
 */
const isZero = (...values: Array<number>) => values.every(value => value === 0);

/**
 * Calculate remaining amount for total transaction amounts
 *
 * @param {number} left Left value
 * @param {number} right Right value
 */
export const getValuePairRemainder = (left: number, right: number): [number, number] => {
  // Both remaining amounts are zero if inputs values are also zeroes
  if (isZero(left, right)) {
    return [0, 0];
  }

  // Calculate the sum of both amounts if left one is negative
  if (isNegative(left) && isPositive(right)) {
    return [Math.abs(left) + right, 0];
  }

  // Calculate the sum of both amounts if right one is negative
  if (isPositive(left) && isNegative(right)) {
    return [0, Math.abs(right) + left];
  }

  // Calculate remainder by comparing both negative values
  if (isNegative(left, right)) {
    const lower = Math.min(left, right);
    return [lower - left, lower - right];
  }

  // Calculate remainder by comparing both positive values
  const upper = Math.max(left, right);
  return [upper - left, upper - right];
};

/**
 * Calculate percentage of two values in comparison to the opposite one
 *
 * @param {number} left Left value
 * @param {number} right Right value
 */
export const getValuePairPercentage = (left: number, right: number): [number, number] => {
  // Return empty percentages if both amounts are zeroes
  if (isZero(left, right)) {
    return [0, 0];
  }

  // Calculate percentages if both of the values are positive
  if (isPositive(left, right)) {
    const upper = Math.max(left, right);
    return [(left / upper) * 100, (right / upper) * 100];
  }

  // Calculate percentages if both of the values are negative
  if (isNegative(left, right)) {
    const lower = Math.min(left, right);
    return [(left / lower) * 100, (right / lower) * 100];
  }

  // Consider 100% of the left amount remaining if it's negative
  if (isNegative(left) && isPositive(right)) {
    return [0, 100];
  }

  // Consider 100% of the right amount remaining if it's negative
  if (isPositive(left) && isNegative(right)) {
    return [100, 0];
  }

  // Skip validation if any of the values are empty
  return [0, 0];
};
